import { Components, Formio } from 'formiojs';
import FieldComponent from 'formiojs/components/_classes/field/Field';

export class FaceDetectionComponent extends FieldComponent {
  private videoStream: MediaStream | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private canvasElement: HTMLCanvasElement | null = null;
  private isCameraActive: boolean = false;
  dataValue: any;
  refs: any;
  addEventListener: any;
  loadRefs: any;
  component: any;

  static schema(...extend: any[]): any {
    return FieldComponent.schema(
      {
        type: 'faceDetection',
        label: 'Face Detection',
        key: 'faceDetection',
        input: true,
        persistent: true,
      },
      ...extend
    );
  }

  static get builderInfo() {
    return {
      title: 'Face Detection',
      group: 'basic',
      icon: 'user',
      weight: 80,
      schema: FaceDetectionComponent.schema(),
    };
  }

  get defaultSchema() {
    return FaceDetectionComponent.schema();
  }

  get inputInfo() {
    const info = super.inputInfo;
    info.type = 'input';
    info.attr = {
      ...info.attr,
      type: 'hidden'
    };
    return info;
  }

  render() {
    return super.render(`
      <div class="face-detection-container">
        <input ref="input" name="${this.component.key}" type="hidden" />
        <div class="video-wrapper" style="margin-bottom: 10px;">
          <video 
            ref="video" 
            autoplay 
            playsinline 
            style="width: 320px; height: 240px; border: 1px solid #ccc; border-radius: 4px;"
          ></video>
          <canvas 
            ref="canvas" 
            style="display: none;"
            width="320" 
            height="240"
          ></canvas>
        </div>
        <div class="controls" style="margin-bottom: 10px;">
          <button type="button" ref="startBtn" class="btn btn-primary btn-sm">Start Camera</button>
          <button type="button" ref="captureBtn" class="btn btn-success btn-sm" disabled>Detect Face</button>
          <button type="button" ref="resetBtn" class="btn btn-danger btn-sm" disabled>Reset</button>
        </div>
        <div ref="status" class="alert alert-info" style="display: none;"></div>
        <div ref="preview" style="margin-top: 10px;"></div>
      </div>
    `);
  }

  attach(element: any) {
    const refs = super.attach(element);

    this.loadRefs(element, {
      video: 'single',
      canvas: 'single',
      startBtn: 'single',
      captureBtn: 'single',
      resetBtn: 'single',
      status: 'single',
      preview: 'single',
      input: 'single'
    });

    // ✅ Force refs.input into an array (Formio expects array)
    if (this.refs?.input && !Array.isArray(this.refs.input)) {
      this.refs.input = [this.refs.input];
    }

    this.videoElement = this.refs.video as HTMLVideoElement;
    this.canvasElement = this.refs.canvas as HTMLCanvasElement;

    if (this.refs.startBtn) {
      this.addEventListener(this.refs.startBtn, 'click', async (e) => {
        e.preventDefault();
        await this.startCamera();
      });
    }

    if (this.refs.captureBtn) {
      this.addEventListener(this.refs.captureBtn, 'click', (e) => {
        e.preventDefault();
        this.detectFace();
      });
    }

    if (this.refs.resetBtn) {
      this.addEventListener(this.refs.resetBtn, 'click', (e) => {
        e.preventDefault();
        this.resetDetection();
      });
    }

    return refs;
  }

  async startCamera() {
    try {
      if (this.isCameraActive) return;

      this.showStatus('Requesting camera access...', 'info');

      this.videoStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 320 },
          height: { ideal: 240 },
          facingMode: 'user'
        }
      });

      if (this.videoElement && this.videoStream) {
        this.videoElement.srcObject = this.videoStream;
        this.isCameraActive = true;

        await this.videoElement.play();
        
        this.showStatus('Camera active - Ready to detect face', 'success');
        if (this.refs.captureBtn) this.refs.captureBtn.disabled = false;
        if (this.refs.resetBtn) this.refs.resetBtn.disabled = false;
        if (this.refs.startBtn) this.refs.startBtn.disabled = true;
      }
    } catch (err) {
      console.error('Camera access error:', err);
      this.showStatus('Camera access denied. Please check permissions.', 'danger');
    }
  }

  async detectFace() {
    if (!this.videoElement || !this.canvasElement) {
      this.showStatus('Video capture failed', 'danger');
      return;
    }

    const context = this.canvasElement.getContext('2d');
    if (!context) return;

    context.drawImage(
      this.videoElement,
      0, 0,
      this.canvasElement.width,
      this.canvasElement.height
    );

    try {
      const imageData = this.canvasElement.toDataURL('image/png');
      
      if (this.refs.preview) {
        this.refs.preview.innerHTML = `
          <img 
            src="${imageData}" 
            style="width: 320px; height: 240px; border: 1px solid #ccc; border-radius: 4px;"
            alt="Detected face"
          />`;
      }

      this.setValue(imageData);
      this.showStatus('Face detected and captured successfully', 'success');
    } catch (err) {
      console.error('Face detection error:', err);
      this.showStatus('Failed to capture face', 'danger');
    }
  }

  resetDetection() {
    this.setValue(null);
    if (this.refs.preview) {
      this.refs.preview.innerHTML = '';
    }
    this.showStatus('Ready to detect face', 'info');
  }

  showStatus(message: string, type: 'info' | 'success' | 'danger') {
    if (this.refs.status) {
      this.refs.status.style.display = 'block';
      this.refs.status.className = `alert alert-${type}`;
      this.refs.status.textContent = message;
    }
  }

  destroy() {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
      this.isCameraActive = false;
    }
    super.destroy();
  }

  normalizeValue(value: any) {
    return value || null;
  }

  updateValue(value?: any, flags?: any) {
    if (value !== undefined) {
      this.dataValue = value;
    } else if (this.refs?.input?.[0]) {
      this.dataValue = this.refs.input[0].value || null;
    }
    return this.dataValue;
  }

  getValue() {
    return this.dataValue || null;
  }

  setValue(value: any, flags: any = {}) {
    this.dataValue = value || null;

    if (this.refs?.input) {
      this.refs.input.forEach((input: any) => {
        input.value = this.dataValue || '';
      });
    }

    return this.dataValue;
  }
}

// Register component
Formio.use({
  components: {
    faceDetection: FaceDetectionComponent
  }
});
